import c from 'classnames';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';

import { ImpersonationBanner } from '@repo/ui';
import { getColors } from 'contexts';
import useHydrateSeenIndicators from 'hooks/updateIndicators/useHydrateSeenIndicators';
import { THRoutes } from 'routes';
import useSignOut from 'services/auth/useSignOut';
import useFeature from 'services/features/useFeature';
import { getFirstName, getLastName } from 'store/account';
import { getHasModalOpened } from 'store/modal';
import {
  getImpersonateId,
  getIsImpersonatorUserAdmin,
  getRoles,
  UserRoles,
} from 'store/user';

import { Spinfinity } from '@repo/ui';
import { MAINTENANCE_MODE, MAINTENANCE_TEXT } from 'assets/env_constants';
import MarketingBanner from 'components/MarketingBanner/MarketingBanner';
import PractitionerSection from 'components/PractitionerSection/PractitionerSection';

import styles from './Layout.module.css';

const MaintenanceBanner = lazy(
  () => import('components/MaintenanceBanner/MaintenanceBanner'),
);

export const Layout = () => {
  useHydrateSeenIndicators();

  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const impersonateId = useSelector(getImpersonateId);
  const firstName = useSelector(getFirstName);
  const lastName = useSelector(getLastName);
  const isImpersonatorUserAdmin = useSelector(getIsImpersonatorUserAdmin);
  const roles = useSelector(getRoles);
  const signOut = useSignOut();

  const isPractitionerImpersonating =
    !!impersonateId &&
    !isImpersonatorUserAdmin &&
    roles?.includes(UserRoles.PRACTITIONER);

  useEffect(() => {
    // Set data attribute on body for global CSS access
    if (isSectionOpen) {
      document.body.setAttribute('data-practitioner-aside-open', 'true');
    } else {
      document.body.removeAttribute('data-practitioner-aside-open');
    }
  }, [isSectionOpen]);

  // Close section when impersonation stops
  useEffect(() => {
    if (!isPractitionerImpersonating && isSectionOpen) {
      setIsSectionOpen(false);
    }
  }, [isPractitionerImpersonating, isSectionOpen]);

  // Auto-open section if openPracSection URL parameter is present
  useEffect(() => {
    if (isPractitionerImpersonating) {
      const urlParams = new URLSearchParams(window.location.search);
      const shouldOpenSection = urlParams.get('openPracSection') === 'true';

      if (shouldOpenSection) {
        setIsSectionOpen(true);
        // Clean up URL parameter after opening
        urlParams.delete('openPracSection');
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [isPractitionerImpersonating]);

  const handleOpenPatientNote = useCallback(() => {
    setIsSectionOpen(true);
  }, []);

  const handleClosePatientNote = useCallback(() => {
    setIsSectionOpen(false);
  }, []);

  const exitImpersonation = useCallback(async () => {
    await signOut();
    window.close();
  }, [signOut]);

  const { isFeatureEnabled: showMarketingBanner, variables: rawVariables } =
    useFeature('results_announcement_banner');
  const [isMarketingBannerVisible, setIsMarketingBannerVisible] =
    useState(showMarketingBanner);

  const { isFeatureEnabled: isPractitionerNoteEnabled } =
    useFeature('practitioner_note');

  const handleMarketingBannerDismiss = () => {
    setIsMarketingBannerVisible(false);
  };

  const variables = rawVariables as { [key: string]: string };

  const modalIdentifier = {
    name: 'marketingBanner',
    props: { text: variables?.text, linkUrl: variables?.linkUrl },
  };

  const isDismissed = useSelector(getHasModalOpened(modalIdentifier));

  const shouldDisplayMarketingBanner =
    showMarketingBanner && isMarketingBannerVisible && !isDismissed;

  useEffect(() => {
    setIsMarketingBannerVisible(showMarketingBanner);
  }, [showMarketingBanner]);

  return (
    <Suspense fallback={<Spinfinity fullScreen />}>
      {MAINTENANCE_MODE === 'true' && (
        <MaintenanceBanner text={MAINTENANCE_TEXT} />
      )}
      <div
        className={c(styles.rootContainer, {
          [styles.hasImpersonationBanner]: !!impersonateId,
          [styles.hasMarketingBanner]: shouldDisplayMarketingBanner,
        })}
      >
        {(!!impersonateId || shouldDisplayMarketingBanner) && (
          <div className={styles.bannersContainer} data-id='banners-container'>
            {!!impersonateId && (
              <ImpersonationBanner
                name={
                  firstName
                    ? firstName + ' ' + lastName?.[0]
                    : 'patient results'
                }
                exit={exitImpersonation}
                isImpersonatorUserAdmin={isImpersonatorUserAdmin}
                onOpenPatientNote={
                  isPractitionerImpersonating && isPractitionerNoteEnabled
                    ? handleOpenPatientNote
                    : undefined
                }
              />
            )}
            {shouldDisplayMarketingBanner && (
              <MarketingBanner
                text={variables?.text}
                linkUrl={variables?.linkUrl}
                isDismissable={!!variables?.isDismissable}
                onDismiss={handleMarketingBannerDismiss}
                modalIdentifier={modalIdentifier}
              />
            )}
          </div>
        )}
        <div className='absolute'>
          <ToastContainer
            toastStyle={{
              backgroundColor: getColors().bg,
              color: getColors().fg,
            }}
            toastClassName={styles.toastOverride}
          />
        </div>
        <div className={styles.mainContent}>
          <THRoutes />
        </div>
        {isPractitionerImpersonating && (
          <PractitionerSection
            isOpen={isSectionOpen}
            onClose={handleClosePatientNote}
          />
        )}
      </div>
    </Suspense>
  );
};
